/**
 * Pure viewport math for the VTT canvas.
 *
 * The VTT treats the canvas CONTAINER as a stable, fixed-size viewport: the
 * PIXI renderer surface fills the container and never changes size with the
 * map. The map/tabletop content lives on `app.stage`, which is panned and
 * zoomed (a {scale, x, y} viewport transform) INSIDE that fixed surface.
 *
 * These helpers compute the pan offset (`stage.position`) that centers the
 * world content within the viewport. They're extracted here — free of DOM and
 * PIXI — so the centering behaviour can be unit-tested directly.
 *
 * Coordinate model:
 *   screen = world * scale + offset
 * so a world point (0,0) maps to `offset`, and the world's far corner
 * (mapPixelW, mapPixelH) maps to `offset + mapPixel * scale`.
 */

export interface ViewportOffset {
  x: number
  y: number
}

/**
 * Compute the pan offset that centers a map of `mapPixelW × mapPixelH` (world
 * pixels, i.e. width*gridSize) within a viewport of `viewportW × viewportH`
 * (the container/renderer size) at a given `scale`.
 *
 * The scaled content size is `mapPixel * scale`. Centering puts equal slack on
 * both sides: offset = (viewport - scaledContent) / 2. When the content is
 * LARGER than the viewport this naturally yields a negative offset (content
 * overflows equally on both edges, still centered), which is the correct
 * behaviour for a "fit-to-center" reset. Callers that want top-left anchoring
 * for oversized maps can clamp the result to <= 0 themselves; the product
 * decision here is to keep it centered at all sizes so reset always recenters.
 */
export function computeCenterOffset(
  viewportW: number,
  viewportH: number,
  mapPixelW: number,
  mapPixelH: number,
  scale: number,
): ViewportOffset {
  const scaledW = mapPixelW * scale
  const scaledH = mapPixelH * scale
  return {
    x: (viewportW - scaledW) / 2,
    y: (viewportH - scaledH) / 2,
  }
}
